import React, { forwardRef, useEffect, useRef, useState } from 'react';
import Rodal from '../../../components/Rodal/Rodal';
import {
    Col,
    FormGroup,
    InputGroupAddon,
    ModalBody,
    ModalHeader,
    Row,
} from 'reactstrap';
import './NdaTermsPopup.scss';
import { useStateSelector } from '../../../store/selectors';
import createNotification from '../../../utils/createNotification';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { LoadMarketPlacePricing } from '../../../slices/marketplace/pricing/load-marketplace-pricing.thunk';
import Helpers from '../../../utils/helper';
import { EngagementProcess } from '../../../slices/marketplace/timeline/models/engagement-process.model';
import { LevelOfService } from '../../../slices/marketplace/timeline/models/enums/level-of-service.enum';

interface ExtendDeadlinePopupProps {
    isVisible: boolean;
    setIsVisible(value: boolean): void;
    engagementId: number;
    fetchData(): void;
    eventId: string;
    eventDeadline: Date;
    engagementProcess: EngagementProcess;
}

export const ExtendDeadlinePopup = (props: ExtendDeadlinePopupProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [isLoading, setIsLoading] = useState(false);
    const dateRef = useRef(null);
    const [selectedDate, setSelectedDate] = useState<Date>(props.eventDeadline);
    const DateControlInput = forwardRef(({ value, onClick }: any, ref: any) => (
        <div ref={ref} className={'btn'} onClick={onClick}>
            {value}
        </div>
    ));
    const dispatch = useDispatch() as any;
    const marketplacePricing = useStateSelector(
        (state) => state.marketplacePricing.pricing
    );
    useEffect(() => {
        dispatch(LoadMarketPlacePricing());
    }, []);

    const extendDeadline = () => {
        setIsLoading(true);
        const utcDate = new Date(Date.UTC(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            selectedDate.getDate()
        ));

        axios
            .post(
            `api/Marketplace/${props.engagementId}/events/${props.eventId}/deadline`,
            utcDate
            )
            .then((response) => {
            if (response.status !== 200) {
                createNotification(
                'Something went wrong during deadline extension',
                'error'
                );
            }
            })
            .finally(() => {
            props.fetchData();
            setIsLoading(false);
            props.setIsVisible(false);
            });
    };

    const calculateLateFee = () => {
        const diff = Math.abs(
            selectedDate.getTime() - props.eventDeadline.getTime()
        );
        const lateDays = Math.ceil(diff / (1000 * 3600 * 24));
        const levelOfService = props.engagementProcess.levelOfService;

        let pricePerDay = 0;

        if (levelOfService === LevelOfService.DiscountBrokerage) {
            pricePerDay =
                marketplacePricing.discountBrokeragePricing.transactionFees
                    .lateFeeAdditionalOccurrence;
        } else if (levelOfService === LevelOfService.FullServiceBrokerage) {
            pricePerDay =
                marketplacePricing.fullServiceBrokeragePricing.transactionFees
                    .lateFeeAdditionalOccurrence;
        }

        const totalPrice = pricePerDay * lateDays;
        return Helpers.GetCurrencyFormat(totalPrice, 2, true);
    };

    return (
        <>
            <Rodal
                visible={props.isVisible}
                onClose={() => props.setIsVisible(false)}
                animation="slideUp"
                className="data-nda-terms-popup"
                showMask={true}
                width={400}
                showCloseButton={true}>
                <ModalHeader>
                    Extend Data NDA Terms Negotiation & Execution Deadline
                </ModalHeader>
                <ModalBody>
                    <div className="extend-deadline-popup">
                        <Row className="mb-3">
                            You have the ability to extend the deadline of the
                            Data DNA terms negotiation by opting to pay late
                            fees.
                        </Row>
                        <Row className="mb-2">
                            <Col md={4} className="p-0">
                                <label>Current Deadline Date:</label>
                            </Col>
                            <Col md={6} className="p-0">
                                {' '}
                                <span>
                                    {moment(props.eventDeadline).format(
                                        'MM/DD/YYYY'
                                    )}
                                </span>
                            </Col>
                        </Row>
                        <Row className="mb-2">
                            <Col md={4} className="p-0">
                                <label>New Deadline Date:</label>
                            </Col>
                            <Col md={6} className="p-0">
                                <FormGroup className={'d-flex small-border'}>
                                    <InputGroupAddon
                                        addonType="prepend"
                                        onClick={() => {
                                            dateRef.current.setOpen(true);
                                        }}>
                                        <div className="input-group-text">
                                            <FontAwesomeIcon
                                                icon={faCalendarAlt}
                                            />
                                        </div>
                                    </InputGroupAddon>
                                    <DatePicker
                                        portalId="event-start-date-portal"
                                        minDate={props.eventDeadline}
                                        ref={dateRef}
                                        className={'form-control'}
                                        selected={selectedDate}
                                        name="startDate"
                                        popperProps={{ strategy: 'fixed' }}
                                        onChange={(value: Date) => {
                                            setSelectedDate(value);
                                        }}
                                        dateFormat="MM/dd/yyyy"
                                        customInput={<DateControlInput />}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row className="mb-4">
                            <Col md={4} className="p-0">
                                {' '}
                                <label>Late Fees:</label>
                            </Col>
                            <Col md={6} className="p-0">
                                {' '}
                                <span>{calculateLateFee()}</span>
                            </Col>
                        </Row>
                        <Row>
                            <div className="buttons-wrapper">
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => props.setIsVisible(false)}>
                                    Cancel
                                </button>
                                <ButtonLoader
                                    disabled={isLoading}
                                    onClick={() => {
                                        extendDeadline();
                                    }}
                                    className="btn btn-primary"
                                    buttonText={'Extend Deadline'}
                                    isLoading={isLoading}
                                    loaderButtonText={'Extend Deadline'}
                                />
                            </div>
                        </Row>
                    </div>
                </ModalBody>
            </Rodal>
        </>
    );
};
